// IMPORTER UNE LISTE dans l'annuaire, en trois temps :
//
//   1. coller des cellules copiées dans Excel, ou choisir un fichier CSV ;
//   2. vérifier quelle colonne va dans quel champ, voir l'aperçu et ce qui
//      sera écarté (doublons, lignes sans nom, adresses invalides) ;
//   3. importer, et lire le rapport.
//
// Le serveur fait tout le travail, aperçu compris (`dryRun`) : ce qu'on voit
// est exactement ce qui sera importé.
import { useState, type ChangeEvent } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, buttonClass, ErrorBanner, Select, Spinner } from '../components/ui';
import { seniorityLabel } from '../personas/PersonaNeeds';
import { TextArea } from '../write/Forms';
import {
  engagementLabel, IMPORT_FIELDS, useImportPeople,
  type ImportField, type ImportReport,
} from './api';

const errorOf = (cause: unknown) =>
  cause instanceof ApiRequestError ? cause.message : 'Une erreur inattendue est survenue.';

/** Lit un fichier texte. Un CSV enregistré par Excel sous Windows n'est pas
 *  toujours en UTF-8 : si des caractères sont illisibles, on relit en Windows-1252. */
async function readFile(file: File): Promise<string> {
  const read = (encoding: string) => new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(r.error);
    r.readAsText(file, encoding);
  });
  const utf8 = await read('utf-8');
  return utf8.includes('�') ? read('windows-1252') : utf8;
}

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

export function ImportPeople({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const run = useImportPeople(projectId);
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [mapping, setMapping] = useState<(ImportField | null)[] | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [preview, setPreview] = useState<ImportReport | null>(null);
  const [done, setDone] = useState<ImportReport | null>(null);

  async function analyse(next: { mapping?: (ImportField | null)[] | null; updateExisting?: boolean; text?: string } = {}) {
    const body = {
      text: next.text ?? text,
      updateExisting: next.updateExisting ?? updateExisting,
      dryRun: true,
      ...((next.mapping ?? mapping) ? { mapping: (next.mapping ?? mapping) as (ImportField | null)[] } : {}),
    };
    try {
      const report = await run.mutateAsync(body);
      setPreview(report);
      setMapping(report.mapping);
    } catch {
      /* l'erreur est affichée sous le formulaire */
    }
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const content = await readFile(file);
    setFileName(file.name);
    setText(content);
    setMapping(null);
    await analyse({ text: content, mapping: null });
  }

  async function confirm() {
    if (!mapping) return;
    try {
      setDone(await run.mutateAsync({ text, mapping, updateExisting, dryRun: false }));
    } catch {
      /* l'erreur est affichée */
    }
  }

  // --- 3. Le rapport ---------------------------------------------------------
  if (done) {
    return (
      <div className="ppl-import">
        <p className="ppl-import-result" role="status">
          <strong>{plural(done.created, 'personne ajoutée', 'personnes ajoutées')}</strong>
          {done.updated ? ` · ${plural(done.updated, 'fiche complétée', 'fiches complétées')}` : ''}
          {done.skipped.length ? ` · ${plural(done.skipped.length, 'ligne écartée', 'lignes écartées')}` : ''}
        </p>
        <Skipped report={done} />
        <Button variant="ghost" onClick={onDone}>Fermer</Button>
      </div>
    );
  }

  // --- 1. Coller ou choisir un fichier ---------------------------------------
  if (!preview) {
    return (
      <div className="ppl-import">
        <p className="zy-texte-muet">
          Dans Excel, sélectionnez le tableau <strong>avec sa ligne d’en-tête</strong> (Prénom, Nom, E-mail…),
          copiez-le et collez-le ici. Ou choisissez un fichier CSV.
        </p>
        <TextArea
          label="Liste collée"
          value={text}
          onChange={(v) => { setText(v); setFileName(null); setMapping(null); }}
          rows={8}
          placeholder={'Prénom\tNom\tE-mail\tFonction\tPool\nLina\tMartin\tlina@exemple.fr\tResponsable logistique\tBêta-testeurs'}
        />
        <div className="ppl-import-actions">
          <label className={`${buttonClass('ghost')} ppl-file`}>
            Choisir un fichier CSV
            <input type="file" accept=".csv,.tsv,.txt,text/csv,text/plain" onChange={(e) => void onFile(e)} />
          </label>
          <Button variant="accent" loading={run.isPending} disabled={!text.trim()} onClick={() => void analyse()}>
            Analyser la liste
          </Button>
        </div>
        {run.error ? <ErrorBanner>{errorOf(run.error)}</ErrorBanner> : null}
      </div>
    );
  }

  // --- 2. Correspondance, aperçu, ce qui sera écarté -------------------------
  const report = preview;
  const toImport = report.created + report.updated;
  const ignored = report.headers.map((_, i) => i).filter((i) => !mapping?.[i]);
  const mapRow = (h: string, i: number) => (
    <label key={`${h}-${i}`} className="ppl-map-row">
      <span className="ppl-map-head">{h || `Colonne ${i + 1}`}</span>
      <span aria-hidden="true">→</span>
      <Select
       
        value={mapping?.[i] ?? ''}
        onChange={(e) => {
          const next = [...(mapping ?? report.mapping)];
          next[i] = (e.target.value || null) as ImportField | null;
          setMapping(next);
          void analyse({ mapping: next });
        }}
      >
        <option value="">Ignorer</option>
        {IMPORT_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
      </Select>
    </label>
  );
  return (
    <div className="ppl-import">
      <p className="zy-texte-muet">
        {fileName ? <>Fichier <strong>{fileName}</strong> · </> : null}
        {plural(report.total, 'ligne lue', 'lignes lues')}. Vérifiez quelle colonne va dans quel champ.
      </p>

      {/* Les colonnes UTILES d'abord ; les autres (fax, adresse, site web… d'un
          carnet d'adresses) sont repliées : on les ouvre pour en reprendre une. */}
      <div className="ppl-map">
        {report.headers.map((h, i) => (mapping?.[i] ? mapRow(h, i) : null))}
      </div>
      {ignored.length > 0 ? (
        <details className="ppl-ignored">
          <summary>
            {plural(ignored.length, 'colonne ignorée', 'colonnes ignorées')} : {ignored.slice(0, 6).map((i) => report.headers[i]).join(', ')}
            {ignored.length > 6 ? '…' : ''}
          </summary>
          <div className="ppl-map">{ignored.map((i) => mapRow(report.headers[i] ?? '', i))}</div>
        </details>
      ) : null}

      <label className="ppl-check">
        <input className="zy-check"
          type="checkbox"
          checked={updateExisting}
          onChange={(e) => { setUpdateExisting(e.target.checked); void analyse({ updateExisting: e.target.checked }); }}
        />
        Compléter les fiches déjà présentes (même e-mail) au lieu de les écarter
      </label>

      {run.isPending ? <Spinner label="Analyse…" /> : null}

      {report.preview.length > 0 ? (
        <div className="zy-table-cadre">
          <table className="zy-table zy-table--dense">
            <caption className="sr-only">Aperçu des premières personnes</caption>
            <thead>
              <tr><th>Nom</th><th>E-mail</th><th>Fonction · rôle</th><th>Profil</th><th>Pool</th></tr>
            </thead>
            <tbody>
              {report.preview.map((p, i) => (
                <tr key={i}>
                  <td>{[p.firstName, p.lastName].filter(Boolean).join(' ')}</td>
                  <td>{p.email ?? '—'}</td>
                  <td>{[p.jobTitle, p.role].filter(Boolean).join(' · ') || '—'}</td>
                  <td>
                    {[seniorityLabel(p.seniority), engagementLabel(p.engagement) ? `engagement ${engagementLabel(p.engagement)?.toLowerCase()}` : null,
                      p.keyUser ? 'key user' : null, p.domainExpert ? 'expert métier' : null].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td>{p.pool ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <p className="ppl-import-result">
        <strong>{plural(report.created, 'personne à ajouter', 'personnes à ajouter')}</strong>
        {report.updated ? ` · ${plural(report.updated, 'fiche à compléter', 'fiches à compléter')}` : ''}
        {report.skipped.length ? ` · ${plural(report.skipped.length, 'ligne écartée', 'lignes écartées')}` : ''}
      </p>
      <Skipped report={report} />

      {run.error ? <ErrorBanner>{errorOf(run.error)}</ErrorBanner> : null}
      <div className="ppl-import-actions">
        <Button variant="ghost" onClick={() => { setPreview(null); run.reset(); }}>Revenir à la liste</Button>
        <Button variant="accent" loading={run.isPending} disabled={toImport === 0} onClick={() => void confirm()}>
          {toImport === 0 ? 'Rien à importer' : `Importer ${plural(toImport, 'personne', 'personnes')}`}
        </Button>
      </div>
    </div>
  );
}

/** Les lignes écartées et les valeurs non reconnues, avec leur numéro de ligne. */
function Skipped({ report }: { report: ImportReport }) {
  if (report.skipped.length === 0 && report.warnings.length === 0) return null;
  return (
    <ul className="ppl-skipped">
      {report.skipped.map((s) => (
        <li key={`s${s.line}`}>
          <span className="ppl-line">Ligne {s.line}</span> {s.name ? <strong>{s.name}</strong> : null} — écartée : {s.reason}
        </li>
      ))}
      {report.warnings.map((w, i) => (
        <li key={`w${w.line}-${i}`} className="ppl-warn">
          <span className="ppl-line">Ligne {w.line}</span> <strong>{w.name}</strong> — {w.warning}
        </li>
      ))}
    </ul>
  );
}
